/* workspace-admin/Directory — Workspace Directory: chooser/list of workspaces.
   "Open" sets the active workspace (backend session) and enters the CRM; "Manage"
   opens the workspace-admin console. Visible to all roles; create gated to
   workspace.create. Topbar provides user context and logout from any role. */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, EmptyState, Modal } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { useRbac } from '../rbac/RbacContext';
import { Gate } from '../rbac/guards';
import { ROLES, roleFromSession } from '../rbac/roles';
import { statusTone, workspaceMembers } from '../platform/platformStore';
import { STATUS_LABEL } from './workspaceConstants.js';
import * as orgApi from '../api/organization.api.js';
import UserMenu from '../shared/auth/UserMenu.jsx';
import { MODULE_CATALOG, MODULE_CATEGORIES, DEFAULT_MODULES } from '../shared/modules.js';

/* M-05: enterprise module picker — extracted to keep Directory CC within bounds. */
function ModulePicker({ selected, onToggle }) {
  return (
    <div style={{ flex: 1 }}>
      <span className="nx-form-label" style={{ display: 'block', marginBottom: 8 }}>Modules</span>
      <div style={{ maxHeight: 260, overflowY: 'auto', paddingRight: 4 }}>
        {MODULE_CATEGORIES.map(cat => {
          const items = MODULE_CATALOG.filter(m => m.category === cat);
          if (!items.length) return null;
          return (
            <div key={cat} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{cat}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {items.map(mod => (
                  <button key={mod.id} type="button"
                    className={'nx-chip' + (selected.includes(mod.id) ? ' is-active' : '')}
                    aria-pressed={selected.includes(mod.id)}
                    onClick={() => onToggle(mod.id)}>
                    <NexusIcon name={selected.includes(mod.id) ? 'Check' : 'Plus'} size={11} />{mod.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 6 }}>
        {selected.length} module{selected.length !== 1 ? 's' : ''} selected · dependencies added automatically
      </div>
    </div>
  );
}

export default function Directory({ data }) {
  const navigate  = useNavigate();
  const toast     = useToast();
  const { role, orgId, can } = useRbac();
  const auth      = window.useAuth();
  const [q, setQ] = useState('');
  const [creating, setCreating]   = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [brandName, setBrandName] = useState('URSA');

  useEffect(() => {
    fetch('/api/v1/public/platform')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.brandName) setBrandName(d.brandName); })
      .catch(() => {});
  }, []);

  const handleLogout = useCallback(async () => {
    if (auth?.signOut) await auth.signOut().catch(() => {});
    navigate('/', { replace: true });
  }, [auth, navigate]);

  /* Role label and tone for UserMenu pill */
  const sessionUser = window.__nx_auth?.user;
  const normalizedRole = roleFromSession(sessionUser?.effectiveRole || sessionUser?.platformRole);
  const roleLabel = normalizedRole === ROLES.SUPER_ADMIN ? 'Super Admin'
                  : normalizedRole === ROLES.ORG_ADMIN   ? 'Org Admin'
                  : normalizedRole === ROLES.WS_ADMIN    ? 'WS Admin'
                  : 'WS Member';
  const roleTone  = normalizedRole === ROLES.SUPER_ADMIN ? 'purple'
                  : normalizedRole === ROLES.ORG_ADMIN   ? 'blue'
                  : 'green';

  /* Context-aware profile link */
  const onProfile = role === ROLES.SUPER_ADMIN
    ? () => navigate('/platform/profile')
    : can('org.view') && orgId
      ? () => navigate('/organization/' + orgId + '/profile')
      : undefined;

  const all    = data.state.workspaces;
  const scoped = role === ROLES.SUPER_ADMIN ? all : all.filter(w => w.org === orgId);
  const rows   = scoped.filter(w => !q || w.name.toLowerCase().includes(q.toLowerCase()));
  const orgName = (id) => data.state.organizations.find(o => o.id === id)?.name || id;

  const openWorkspace = async (w) => {
    setOpeningId(w.id);
    try {
      if (auth?.switchWorkspace) await auth.switchWorkspace(w.id);
      toast({ tone: 'success', icon: 'LayoutGrid', title: 'Workspace: ' + w.name, desc: 'Now active' });
      let dest = '/app/' + w.id;
      try {
        const saved = sessionStorage.getItem('nx-deep-link');
        if (saved && saved.startsWith('/') && !saved.startsWith('//')) {
          sessionStorage.removeItem('nx-deep-link');
          dest = saved;
        }
      } catch { /* sessionStorage unavailable */ }
      navigate(dest);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to open workspace' });
    } finally {
      setOpeningId(null);
    }
  };

  const toggleModule = (m) => setCreating(c => ({ ...c, modules: c.modules.includes(m) ? c.modules.filter(x => x !== m) : [...c.modules, m] }));

  return (
    <>
      {/* Topbar: sticky full-width header with brand, user context, and logout */}
      <div className="nx-wsdir-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/ursa-logo.svg" alt="URSA" style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'contain' }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--nx-text)' }}>{brandName}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Back to org/platform for admins — workspace users stay here */}
          {role === ROLES.SUPER_ADMIN && (
            <Btn variant="ghost" size="sm" icon="ChevronLeft" onClick={() => navigate('/platform')}>
              Control Center
            </Btn>
          )}
          {role !== ROLES.SUPER_ADMIN && can('org.view') && orgId && (
            <Btn variant="ghost" size="sm" icon="ChevronLeft" onClick={() => navigate('/organization/' + orgId)}>
              Organization
            </Btn>
          )}
          <UserMenu roleLabel={roleLabel} roleTone={roleTone} onLogout={handleLogout} onProfile={onProfile} />
        </div>
      </div>

      <div className="nx-wsdir">
      <div className="nx-wsdir-head">
        <div>
          <h1 className="nx-wsdir-title">Workspaces</h1>
          <p className="nx-wsdir-sub">{scoped.length} workspace{scoped.length === 1 ? '' : 's'}{role !== ROLES.SUPER_ADMIN ? ' in ' + orgName(orgId) : ' across all organizations'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" icon="ArrowRight" onClick={() => { const wsId = window.__nx_auth?.user?.activeWorkspaceId; navigate(wsId ? '/app/' + wsId : '/workspaces'); }}>Open CRM</Btn>
          <Gate action="workspace.create">
            <Btn variant="primary" icon="Plus" onClick={() => setCreating({ name: '', org: orgId, modules: DEFAULT_MODULES })}>Create Workspace</Btn>
          </Gate>
        </div>
      </div>

      <div className="nx-search-inline" style={{ width: 320, margin: '0 auto 22px' }}>
        <NexusIcon name="Search" size={13} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search workspaces…" aria-label="Search workspaces" />
      </div>

      {rows.length === 0 ? (
        <EmptyState icon="LayoutGrid" title="No workspaces match" desc="Try a different search term." />
      ) : (
        <div className="nx-wsdir-grid">
          {rows.map(w => {
            const memberCount = workspaceMembers(data.state, w.id).length || w.members;
            return (
              <div key={w.id} className="nx-wsdir-card">
                <div className="nx-wsdir-card-top">
                  <span className="nx-wsdir-mark">{w.name?.[0] ?? '?'}</span>
                  <Pill tone={statusTone((w.status ?? '').toUpperCase())}>{STATUS_LABEL[w.status] ?? w.status}</Pill>
                </div>
                <div className="nx-wsdir-name">{w.name}</div>
                {role === ROLES.SUPER_ADMIN && <div className="nx-wsdir-org">{orgName(w.org)}</div>}
                <div className="nx-wsdir-meta">
                  <span><NexusIcon name="Users" size={12} /> {memberCount} members</span>
                  <span><NexusIcon name="Layers" size={12} /> {w.modules.length} modules</span>
                </div>
                <div className="nx-wsdir-modules">
                  {w.modules.slice(0, 4).map(m => <span key={m} className="nx-org-ws-module">{m}</span>)}
                  {w.modules.length > 4 && <span className="nx-org-ws-module">+{w.modules.length - 4}</span>}
                </div>
                <div className="nx-wsdir-foot">
                  <Btn variant="primary" size="sm" icon="ArrowRight"
                    disabled={openingId === w.id || w.status === 'ARCHIVED'}
                    title={w.status === 'ARCHIVED' ? 'Archived workspaces cannot be opened' : undefined}
                    aria-label={'Open ' + w.name + ' in CRM'}
                    onClick={() => openWorkspace(w)}>{openingId === w.id ? 'Opening…' : 'Open'}</Btn>
                  <Gate action="membership.view">
                    <Btn variant="ghost" size="sm" icon="Settings" onClick={() => navigate('/workspaces/' + w.id)}>Manage</Btn>
                  </Gate>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <Modal title="Create Workspace" icon="LayoutGrid" onClose={() => setCreating(null)}>
          <div className="nx-form-row" style={{ marginTop: 4 }}>
            <label htmlFor="ws-create-name">Workspace Name</label>
            <input id="ws-create-name" className="nx-input" autoFocus value={creating.name} onChange={e => setCreating({ ...creating, name: e.target.value })} placeholder="e.g. Retail Lending" />
          </div>
          {role === ROLES.SUPER_ADMIN && (
            <div className="nx-form-row">
              <label htmlFor="ws-create-org">Organization</label>
              <select id="ws-create-org" className="nx-input" value={creating.org} onChange={e => setCreating({ ...creating, org: e.target.value })}>
                {data.state.organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}
          <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
            <ModulePicker selected={creating.modules} onToggle={toggleModule} />
          </div>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setCreating(null)}>Cancel</Btn>
            <Btn variant="primary" icon="Check" onClick={async () => {
              if (!creating.name.trim()) return;
              try {
                const res = await orgApi.createWorkspace(creating.org, { name: creating.name, modules: creating.modules });
                const id = res?.id || ('ws_' + Date.now());
                data.update(s => ({ ...s, workspaces: [{ id, org: creating.org, name: creating.name, members: 0, modules: creating.modules, status: 'ONBOARDING', created: new Date().toISOString().slice(0, 10) }, ...s.workspaces] }));
                data.addAudit({ action: 'created workspace', target: (data.state.organizations.find(o => o.id === creating.org)?.name || '') + ' · ' + creating.name, cat: 'Org' });
                toast({ tone: 'success', icon: 'Check', title: 'Workspace Created' });
                setCreating(null);
              } catch (err) {
                toast({ tone: 'error', icon: 'AlertCircle', title: 'Create failed', desc: err.message });
              }
            }}>Create</Btn>
          </div>
        </Modal>
      )}
      </div>{/* end nx-wsdir */}
    </>
  );
}
